import React, { useEffect, useState, useRef } from 'react'
import { useQueryParams } from '@fs/zion-router'
import { appPath } from '@fs/zion-config'
import axios from '@fs/zion-axios'
import { H3, MarginBox, Subheading } from '@fs/zion-ui'
import { i18n } from '@fs/zion-locale'
import { usePlaceReportsPageData } from '../PlaceReportsProvider'

const emptyLocales = []
function extractGuidelinesLocales(reports = []) {
  return reports
    .map((report) => {
      const match = report.match(/guidelines_(\w{2})\.md/)
      return match ? match[1] : null
    })
    .filter((locale) => locale)
}

function extractRegionSummaryLocales(reports = []) {
  return reports
    .map((report) => {
      const match = report.match(/region_summary_(\w{2})\.md/)
      return match ? match[1] : null
    })
    .filter((locale) => locale)
}

function getMarkdownPathFromArgs(locale, region, country, division) {
  if (region && country && division) {
    return `regions/${region}/countries/${country}/divisions/${division}/guidelines_${locale}.md`
  }
  if (region && country) {
    return `regions/${region}/countries/${country}/guidelines_${locale}.md`
  }

  if (region) {
    return `regions/${region}/region_summary_${locale}.md`
  }
  return null
}

export default function useMarkdownContent() {
  const [markdown, setMarkdown] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const { query } = useQueryParams()
  const { region, country, division } = query
  const { getRegion, getCountryAndRegion, getDivision, selectedLocale, setSelectedLocale } = usePlaceReportsPageData()
  const [locales, setLocales] = useState(emptyLocales)
  const isFetching = useRef(false)

  function getMarkdownPath(locale) {
    return getMarkdownPathFromArgs(locale, region, country, division)
  }

  async function fetchMarkdown(markdownPath) {
    setLoading(true)
    setError(null)
    try {
      const url = `${appPath}/place-reports/markdown-content/?filename=${markdownPath}`
      const response = await axios.get(url)
      if (response.status !== 200) {
        throw new Error(`Network response was not ok ${response.status} - ${response.statusText} - ${markdownPath}`)
      }
      setMarkdown(response.data.content)
    } catch (err) {
      setError(
        <MarginBox marginX="nano" marginY="nano">
          <H3 size="H5">
            {`File Missing - ${markdownPath}`}
            <Subheading>{err.message}</Subheading>
          </H3>
        </MarginBox>
      )
      setMarkdown(null)
    } finally {
      setLoading(false)
      isFetching.current = false
    }
  }

  useEffect(() => {
    function getNewSelectedLocale(extractedLocales = []) {
      const i18nLanguageOrFalse = extractedLocales.includes(i18n.language) ? i18n.language : false
      const firstLocaleOrFalse = extractedLocales[0] || false
      return i18nLanguageOrFalse || firstLocaleOrFalse || 'en'
    }

    let extractedLocales = emptyLocales
    if (region && country && division) {
      extractedLocales = extractGuidelinesLocales(getDivision(division)?.division?.reports)
    } else if (region && country) {
      extractedLocales = extractGuidelinesLocales(getCountryAndRegion(country)?.country?.reports)
    } else if (region) {
      extractedLocales = extractRegionSummaryLocales(getRegion(region)?.reports)
    }
    const newSelectedLocale = getNewSelectedLocale(extractedLocales)
    setLocales(extractedLocales)
    setSelectedLocale(newSelectedLocale)
    const markdownPath = getMarkdownPathFromArgs(newSelectedLocale, region, country, division)
    if (markdownPath) {
      fetchMarkdown(markdownPath)
    } else {
      setMarkdown(null)
      setError(null)
    }
  }, [region, country, division, getRegion, getCountryAndRegion, getDivision, setSelectedLocale])

  return {
    markdown,
    error,
    loading,
    region,
    locales,
    selectedLocale,
    setSelectedLocale,
    fetchMarkdown,
    getMarkdownPath,
  }
}
